import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useTranslation } from "@helpers/translate";
import CryptoMarketCapOverview from "./components/CryptoMarketCapOverview";
import CryptoMarketCapBreakdown from "./components/CryptoMarketCapBreakdown";
import { getMonthLabel } from "@utils/cryptoMarketCap/monthLabels";
import config from "../../../../../../../config";
import "./index.sass";

const FILTER_DAYS = {
  "1M": 30,
  "1Y": 365,
  ALL: 3650,
};

const formatDateParam = (date) => date.toISOString();

const getDateRangeByFilter = (filter) => {
  const timeEnd = new Date();
  const timeStart = new Date();

  timeStart.setDate(timeStart.getDate() - FILTER_DAYS[filter]);

  return {
    time_start: formatDateParam(timeStart),
    time_end: formatDateParam(timeEnd),
  };
};

const formatCompactUsdValue = (value) => {
  const number = Number(value) || 0;

  if (number >= 1_000_000_000_000) {
    return `$${(number / 1_000_000_000_000).toFixed(2)}T`;
  }

  return `$${(number / 1_000_000_000).toFixed(2)}B`;
};

const getTooltipDateLabel = (date, translate) => {
  return `${date.getDate()} ${getMonthLabel(
    date.getMonth(),
    translate,
  )} ${date.getFullYear()}`;
};

const getXAxisLabels = (data, filter, translate) => {
  return data.map((item, index) => {
    const currentDate = item.date;

    if (filter === "1M") {
      return index % 5 === 0
        ? `${String(currentDate.getDate()).padStart(2, "0")}/${String(
            currentDate.getMonth() + 1,
          ).padStart(2, "0")}`
        : "";
    }

    if (filter === "1Y") {
      return index % 30 === 0
        ? getMonthLabel(currentDate.getMonth(), translate)
        : "";
    }

    if (filter === "ALL") {
      return index % 365 === 0 ? String(currentDate.getFullYear()) : "";
    }

    return item.label;
  });
};

const buildChartConfig = (series, filter, translate) => {
  const chartWidth = 500;
  const chartHeight = 200;

  const allValues = series.flatMap((item) =>
    item.data.map((point) => point.value),
  );

  if (!allValues.length) {
    return {
      chartWidth,
      chartHeight,
      series: [],
      yAxisLabels: [],
      xAxisLabels: [],
    };
  }

  const minValue = Math.min(...allValues);
  const maxValue = Math.max(...allValues);
  const valueRange = maxValue - minValue || 1;

  const preparedSeries = series.map((item) => {
    const pointsData = item.data.map((point, index) => {
      const x =
        item.data.length === 1
          ? chartWidth / 2
          : (index / (item.data.length - 1)) * chartWidth;

      const topOffset = 12;
      const bottomOffset = 10;
      const drawableHeight = chartHeight - topOffset - bottomOffset;

      const y =
        topOffset +
        (drawableHeight -
          ((point.value - minValue) / valueRange) * drawableHeight);

      return {
        ...point,
        x,
        y,
      };
    });

    const points = pointsData.map((point) => `${point.x},${point.y}`).join(" ");

    return {
      ...item,
      points,
      pointsData,
      areaPoints: `${points} ${chartWidth},${chartHeight} 0,${chartHeight}`,
    };
  });

  return {
    chartWidth,
    chartHeight,
    series: preparedSeries,
    yAxisLabels: [
      formatCompactUsdValue(maxValue),
      formatCompactUsdValue((maxValue + minValue) / 2),
      formatCompactUsdValue(minValue),
    ],
    xAxisLabels: getXAxisLabels(series[0]?.data || [], filter, translate),
  };
};

const CryptoMarketCap = () => {
  const { __ } = useTranslation();

  const [activeView, setActiveView] = useState("overview");
  const [activeTimeFilter, setActiveTimeFilter] = useState("1M");
  const [quotes, setQuotes] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchGlobalMetricsHistory = async () => {
      const { time_start, time_end } = getDateRangeByFilter(activeTimeFilter);

      setIsLoading(true);

      try {
        const response = await axios.get(
          `${config.api_url}/coinmarketcap/global-metrics/history`,
          {
            params: {
              time_start,
              time_end,
              interval: "daily",
              convert: "USD",
            },
          },
        );

        setQuotes(response.data?.data?.quotes || []);
      } catch (error) {
        console.error("Failed to fetch global metrics history:", error);
        setQuotes([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchGlobalMetricsHistory();
  }, [activeTimeFilter]);

  const chartData = useMemo(() => {
    return quotes.map((item) => {
      const usd = item.quote?.USD || {};
      const date = new Date(item.timestamp);

      const totalMarketCap = Number(usd.total_market_cap) || 0;

      const tradingVolume = Number(usd.total_volume_24h) || 0;

      const btcDominance = Number(item.btc_dominance) || 0;

      const ethDominance = Number(item.eth_dominance) || 0;

      const otherDominance = Math.max(100 - btcDominance - ethDominance, 0);

      const bitcoinValue = (totalMarketCap * btcDominance) / 100;

      const ethereumValue = (totalMarketCap * ethDominance) / 100;

      const othersValue = (totalMarketCap * otherDominance) / 100;

      return {
        date,
        label: `${date.getDate()} ${getMonthLabel(date.getMonth(), __)}`,
        tooltipDate: getTooltipDateLabel(date, __),
        totalMarketCap,
        tradingVolume,
        btcDominance,
        ethDominance,
        otherDominance,
        bitcoinValue,
        ethereumValue,
        othersValue,
        bitcoin: formatCompactUsdValue(bitcoinValue),
        ethereum: formatCompactUsdValue(ethereumValue),
        others: formatCompactUsdValue(othersValue),
      };
    });
  }, [quotes, __]);

  const latestData = chartData[chartData.length - 1];

  const overviewMetrics = useMemo(
    () => ({
      totalMarketCap: formatCompactUsdValue(latestData?.totalMarketCap),
      tradingVolume: formatCompactUsdValue(latestData?.tradingVolume),
    }),
    [latestData],
  );

  const overviewChartConfig = useMemo(
    () =>
      buildChartConfig(
        [
          {
            name: "Overview",
            color: "#10b981",
            data: chartData.map((item) => ({
              date: item.date,
              label: item.label,
              tooltipDate: item.tooltipDate,
              value: item.totalMarketCap,
              marketCap: formatCompactUsdValue(item.totalMarketCap),
              volume: formatCompactUsdValue(item.tradingVolume),
            })),
          },
        ],
        activeTimeFilter,
        __,
      ),
    [chartData, activeTimeFilter, __],
  );

  const breakdownLegendData = useMemo(() => {
    return [
      {
        name: "Bitcoin",
        value: latestData?.bitcoinValue || 0,
        color: "#f59e0b",
      },
      {
        name: "Ethereum",
        value: latestData?.ethereumValue || 0,
        color: "#6366f1",
      },
      {
        name: __("markets.cryptoMarketCapOthers"),
        value: latestData?.othersValue || 0,
        color: "#10b981",
      },
    ];
  }, [latestData]);

  const breakdownChartConfig = useMemo(
    () =>
      buildChartConfig(
        [
          {
            name: "Bitcoin",
            color: "#f59e0b",
            data: chartData.map((item) => ({
              date: item.date,
              label: item.label,
              tooltipDate: item.tooltipDate,
              value: item.bitcoinValue,
              bitcoin: item.bitcoin,
              ethereum: item.ethereum,
              others: item.others,
            })),
          },
          {
            name: "Ethereum",
            color: "#6366f1",
            data: chartData.map((item) => ({
              date: item.date,
              label: item.label,
              tooltipDate: item.tooltipDate,
              value: item.ethereumValue,
              bitcoin: item.bitcoin,
              ethereum: item.ethereum,
              others: item.others,
            })),
          },
          {
            name: "Others",
            color: "#10b981",
            data: chartData.map((item) => ({
              date: item.date,
              label: item.label,
              tooltipDate: item.tooltipDate,
              value: item.othersValue,
              bitcoin: item.bitcoin,
              ethereum: item.ethereum,
              others: item.others,
            })),
          },
        ],
        activeTimeFilter,
        __,
      ),
    [chartData, activeTimeFilter, __],
  );

  return (
    <div className="crypto-market-cap">
      <div className="crypto-market-cap__header">
        <h2 className="crypto-market-cap__title">
          {__("markets.cryptoMarketCapTitle")}
        </h2>

        <div className="crypto-market-cap__tabs">
          <button
            className={`crypto-market-cap__tab ${
              activeView === "overview" ? "crypto-market-cap__tab--active" : ""
            }`}
            onClick={() => setActiveView("overview")}
          >
            {__("markets.cryptoMarketCapOverview")}
          </button>

          <button
            className={`crypto-market-cap__tab ${
              activeView === "breakdown" ? "crypto-market-cap__tab--active" : ""
            }`}
            onClick={() => setActiveView("breakdown")}
          >
            {__("markets.cryptoMarketCapBreakdown")}
          </button>
        </div>
      </div>

      <div className="crypto-market-cap__stats">
        {activeView === "overview" ? (
          <>
            <div className="crypto-market-cap__stat">
              <span className="crypto-market-cap__stat-label">
                {__("markets.cryptoMarketCapMarketCap")}
              </span>

              <span className="crypto-market-cap__stat-value">
                {overviewMetrics.totalMarketCap}
              </span>
            </div>

            <div className="crypto-market-cap__stat">
              <span className="crypto-market-cap__stat-label">
                {__("markets.cryptoMarketCapVolume")}
              </span>

              <span className="crypto-market-cap__stat-value">
                {overviewMetrics.tradingVolume}
              </span>
            </div>
          </>
        ) : (
          breakdownLegendData.map((item) => (
            <div key={item.name} className="crypto-market-cap__stat">
              <span className="crypto-market-cap__stat-label-wrap">
                <span
                  className="crypto-market-cap__stat-label-dot"
                  style={{
                    backgroundColor: item.color,
                  }}
                  aria-hidden="true"
                />

                <span className="crypto-market-cap__stat-label">
                  {item.name}
                </span>
              </span>

              <span className="crypto-market-cap__stat-value">
                {formatCompactUsdValue(item.value)}
              </span>
            </div>
          ))
        )}

        <div className="crypto-market-cap__periods">
          {[
            {
              value: "1M",
              label: __("markets.cryptoMarketCap30D"),
            },
            {
              value: "1Y",
              label: __("markets.cryptoMarketCap1Y"),
            },
            {
              value: "ALL",
              label: __("markets.cryptoMarketCapAll"),
            },
          ].map(({ value, label }) => (
            <button
              key={value}
              className={`crypto-market-cap__period ${
                activeTimeFilter === value
                  ? "crypto-market-cap__period--active"
                  : ""
              }`}
              onClick={() => setActiveTimeFilter(value)}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="crypto-market-cap__chart-placeholder">
        {isLoading ? (
          <div className="crypto-market-cap__loader-wrap">
            <span className="crypto-market-cap__loader" />
          </div>
        ) : activeView === "overview" ? (
          <CryptoMarketCapOverview
            key={`overview-${activeTimeFilter}`}
            chartConfig={overviewChartConfig}
          />
        ) : (
          <CryptoMarketCapBreakdown
            key={`breakdown-${activeTimeFilter}`}
            chartConfig={breakdownChartConfig}
          />
        )}
      </div>
    </div>
  );
};

export default CryptoMarketCap;
